/**
 * Tipos para el panel "Data Health" (curación de productos catalogados).
 *
 * Spec:
 *  - docs/architecture/DATA_HEALTH_PANEL_SPEC.md (UX)
 *  - docs/architecture/VISIBILITY_AND_HISTORY_SPEC.md (backend)
 *
 * v1 MVP: Ocultos + Dudosos + Reportes (re-skin).
 */

// ----------------------------------------------------------------------------
// Visibility
// ----------------------------------------------------------------------------

export type Visibility = 1 | 0;

export type HiddenReason =
  | 'hidden_out_of_scope'
  | 'hidden_low_confidence'
  | 'hidden_manual'
  | 'hidden_duplicate'
  // Backend devuelve sin prefijo `hidden_` desde v2.16.0 — aceptamos ambos para retro-compat
  | 'out_of_scope'
  | 'low_confidence'
  | 'manual'
  | 'duplicate';

// Reason "corta" que la UI muestra al usuario (sin el prefijo hidden_).
export type HiddenReasonShort = 'out_of_scope' | 'low_confidence' | 'manual' | 'duplicate';

export const HIDDEN_REASON_LABELS: Record<HiddenReason, string> = {
  hidden_out_of_scope: 'Fuera de scope',
  hidden_low_confidence: 'Baja confianza',
  hidden_manual: 'Manual',
  hidden_duplicate: 'Duplicado',
  out_of_scope: 'Fuera de scope',
  low_confidence: 'Baja confianza',
  manual: 'Manual',
  duplicate: 'Duplicado',
};

export const HIDDEN_REASON_BADGE_CLASS: Record<HiddenReason, string> = {
  hidden_out_of_scope: 'bg-gray-200 text-gray-700 border-gray-300',
  hidden_low_confidence: 'bg-amber-100 text-amber-700 border-amber-200',
  hidden_manual: 'bg-purple-100 text-purple-700 border-purple-200',
  hidden_duplicate: 'bg-orange-100 text-orange-700 border-orange-200',
  out_of_scope: 'bg-gray-200 text-gray-700 border-gray-300',
  low_confidence: 'bg-amber-100 text-amber-700 border-amber-200',
  manual: 'bg-purple-100 text-purple-700 border-purple-200',
  duplicate: 'bg-orange-100 text-orange-700 border-orange-200',
};

// ----------------------------------------------------------------------------
// History events
// ----------------------------------------------------------------------------

export type EventType =
  | 'catalog_created'
  | 'catalog_recataloged'
  | 'manual_edit'
  | 'visibility_changed'
  | 'auto_hide_rule_applied'
  | 'toma_first_seen'
  | 'outlier_flag'
  | 'url_corrected';

export type ActorKind = 'user' | 'system' | 'llm' | 'classifier';

export interface HistoryEvent {
  id: string;
  eventType: EventType;
  actor: string | null; // uid o name o "system"
  actorKind: ActorKind;
  payload: Record<string, unknown>;
  createdAt: string; // ISO
}

// ----------------------------------------------------------------------------
// Catalog rows (lo que listan Ocultos / Dudosos)
// ----------------------------------------------------------------------------

/**
 * DataHealthItem: shape mínimo que la UI de listas necesita.
 *
 * El backend devuelve filas más anchas (toma_* + catalog JOIN) — acá tipamos
 * los campos que ya consumimos hoy. Si el backend incluye más, los aceptamos
 * vía índice de signature para no romper.
 */
export interface DataHealthItem {
  id_catalogo: string;
  title: string | null;
  sitio: string | null;
  imagen?: string | null;
  url?: string | null;
  sale_price?: number | null;

  // Datos TOMA (origen)
  marca?: string | null;
  cat?: string | null;

  // Datos CATALOG (enriched)
  catalog_brand?: string | null;
  catalog_category?: string | null;
  subcategory?: string | null;
  segmento?: string | null;
  contenido?: string | null;
  udm?: string | null;
  confidence_score?: number | null;

  // Visibility
  visibility?: Visibility;
  hidden_reason?: HiddenReason | null;
  hidden_at?: string | null;
  hidden_by?: string | null;

  // Run / catalogado
  last_run_id?: string | null;
  last_catalog_at?: string | null;
  last_hidden_run_id?: string | null;

  // Flags / validation (para tab Dudosos)
  manually_edited_fields?: string[] | null;
  validation_v2?: Record<string, unknown> | null;

  // Allow extra fields del backend sin romper TS.
  [key: string]: unknown;
}

// ----------------------------------------------------------------------------
// List / filter requests
// ----------------------------------------------------------------------------

export type DubiousReason =
  | 'subcat_null'
  | 'low_confidence'
  | 'contenido_out_of_range'
  | 'brand_not_whitelisted'
  | 'forced_to_otros'
  | 'llm_error';

export interface ListFilters {
  page?: number;
  limit?: number;
  reason?: HiddenReasonShort | 'all'; // tab Ocultos
  dubiousReason?: DubiousReason | 'all'; // tab Dudosos
  search?: string;
  sitio?: string | null;
  marca?: string | null;
  subcat?: string | null;
  /** Orden del servidor: columna (DataHealthColumn.key) + dirección. */
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc' | null;
}

export interface ListResponse {
  items: DataHealthItem[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// ----------------------------------------------------------------------------
// Stats
// ----------------------------------------------------------------------------

export interface HiddenStats {
  visible: number;
  hidden_total: number;
  by_reason: Partial<Record<HiddenReason, number>>;
}

// ----------------------------------------------------------------------------
// Bulk actions
// ----------------------------------------------------------------------------

export interface BulkActionResult {
  ok: boolean;
  hidden_count?: number;
  unhidden_count?: number;
  skipped_already_hidden?: number;
  failed?: { id: string; error: string }[];
}
